import React, { memo } from "react";
import { useApp } from "@/contexts/AppContext";
import { AlertTriangle, Radio, Twitter, Instagram, Facebook, Linkedin, Newspaper, MessageCircle, Youtube } from "lucide-react";

const PLATFORM_CONFIG = {
  twitter: { icon: Twitter, color: "#1DA1F2" },
  youtube: { icon: Youtube, color: "#FF0000" },
  instagram: { icon: Instagram, color: "#E4405F" },
  facebook: { icon: Facebook, color: "#1877F2" },
  reddit: { icon: MessageCircle, color: "#FF4500" },
  linkedin: { icon: Linkedin, color: "#0A66C2" },
  news: { icon: Newspaper, color: "#4B5563" }
  // rss: { icon: Rss, color: "#F26522" }
};

const SEV_COLOR = { critical: "var(--sev-critical)", high: "var(--sev-high)", medium: "var(--sev-medium)", low: "var(--sev-low)" };

function LiveSignalTicker() {
  const { tickerSignals, wsStatus, setSelectedPost } = useApp();
  const items = tickerSignals.length ? tickerSignals : [];

  // Use a fixed length for doubling to prevent layout shifts during animation
  const doubled = items.length ? [...items, ...items] : [];

  return (
    <div
      data-testid="live-signal-ticker"
      className="ticker-pause border-b border-hair bg-[var(--bg-surface)] overflow-hidden h-[34px]"
    >
      <div className="flex items-stretch h-full">
        <div
          className="flex items-center gap-2 px-3 py-1 bg-[var(--sev-critical)] font-display tracking-widest text-[10px] uppercase flex-shrink-0"
          style={{ color: "#fff" }}
        >
          <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ background: "#fff" }} />
          Live
        </div>
        <div className="flex items-center gap-2 px-3 py-1 border-r border-hair label-micro flex-shrink-0">
          <Radio className={`w-3 h-3 ${wsStatus.connected ? "text-[var(--sev-low)]" : "text-[var(--sev-medium)]"}`} />
          <span className="min-w-[80px]">{wsStatus.connected ? "UPLINK" : "RECONNECTING"}</span>
        </div>
        <div className="flex-1 overflow-hidden relative">
          <div className="ticker-track py-1">
            {doubled.length > 0 ? doubled.map((s, i) => (
              <button
                key={`${s.id || i}-${i}`}
                type="button"
                onClick={() => setSelectedPost(s)}
                data-testid={`ticker-item-${i}`}
                className="inline-flex items-center gap-2 text-[11px] hover:bg-black/5 rounded-sm px-1 py-0.5 transition-colors cursor-pointer pointer-events-auto relative z-10"
                style={{ color: s._alert ? "var(--sev-critical)" : "var(--text-secondary)" }}
              >
                {s._alert && <AlertTriangle className="w-3 h-3" />}
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ background: SEV_COLOR[s.severity] || "rgba(0,0,0,0.3)" }}
                />
                {(() => {
                  const config = PLATFORM_CONFIG[s.platform] || { icon: Newspaper, color: "var(--text-muted)" };
                  const Icon = config.icon;
                  return <Icon className="w-2.5 h-2.5" style={{ color: config.color }} />;
                })()}
                <span className="uppercase tracking-wide text-[var(--text-muted)]">{s.platform || "—"}</span>
                <span className="text-[var(--text-muted)]">·</span>
                <span className="uppercase text-[var(--text-secondary)]">{s.state || s.venture || "GLOBAL"}</span>
                <span className="text-[var(--text-muted)]">›</span>
                <span className={`truncate max-w-[280px] ${s._alert ? "font-semibold" : ""}`}>
                  {(s.content || "Signal detected").slice(0, 80)}
                </span>
              </button>
            )) : (
              <div className="flex items-center gap-8 opacity-20 select-none">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="flex items-center gap-2 animate-pulse">
                    <div className="w-2 h-2 rounded-full bg-black" />
                    <div className="w-20 h-2 bg-black rounded-full" />
                    <div className="w-48 h-2 bg-black rounded-full" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(LiveSignalTicker);
